import React, { FunctionComponent, useCallback, useState } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import Breakpoints from '../../../constants/breakpoints.constants';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { getImageField } from '../../../utils/getImageFields';
import { targetLinkdsk } from '../../../utils/targetDskLink';
import { getThumbnailProps } from '../../../utils/youtube.utils';
import CustomExternalAnchor from '../../@shared/customExternalAnchor';
import { CustomText } from '../../@shared/customText';
import Icon from '../../@shared/icon';
import ModalDialog from '../../@shared/modalDialog';
import OverlayCarouselPopup from '../../@shared/overlayCarouselPopup';
import { SpecialOffersSlideModel } from '../models/cardCarousel.model';
import CardCarouselSlideButtons from './cardCarouselSlideButtons';
import MediaElement from './mediaElement';
import { TDefaultCardImageProps } from '../types/cardCarousel.types';
import SplitModuleContainer from '../../@shared/splitModuleContainer';

const CarouselSlide: FunctionComponent<{
  model: SpecialOffersSlideModel;
  isEE?: boolean;
  iconimage: string;
  defaultCardImage: TDefaultCardImageProps | undefined;
}> = ({ model, isEE, iconimage, defaultCardImage }): JSX.Element => {
  const [showOverlay, setShowOverlay] = useState(false);
  const isWide = useMediaQuery(`(min-width: ${Breakpoints['tbl-p']}px)`);
  const [showSplitModule, setShowSplitModule] = useState(false);
  const handleSplitModuleModal = (state: boolean): void => setShowSplitModule(state);

  const { thumbnailImgSrc } = getThumbnailProps(model.video?.value?.href || '');
  const closeOverlayModal = (): void => setShowOverlay(false);

  const desktopImageOrDefaultImage = () => {
    if (model.useVideo && model?.video?.value?.href) {
      return thumbnailImgSrc ? model.desktopImage : defaultCardImage?.desktopImage;
    }
    return model?.desktopImage?.value?.src ? model.desktopImage : defaultCardImage?.desktopImage;
  };

  const mobileImageOrDefaultImage = () => {
    if (model.useVideo && model?.video?.value?.href) {
      return thumbnailImgSrc ? model.mobileImage : defaultCardImage?.mobileImage;
    }
    return model?.mobileImage?.value?.src ? model.mobileImage : defaultCardImage?.mobileImage;
  };

  const showOverlayVideo = (): void => {
    if (!model.useVideo) return;
    setShowOverlay(true);
  };

  const getOverlayProps = useCallback(() => {
    return [
      {
        desktopImage: getImageField(model.desktopImage, thumbnailImgSrc || '', model.useVideo),
        mobileImage: getImageField(model.mobileImage, thumbnailImgSrc || '', model.useVideo),
        fullImage: getImageField(model.desktopImage, thumbnailImgSrc || '', model.useVideo),
        desktopAlt: `${model.desktopImage?.value?.alt}`,
        mobileAlt: `${model.mobileImage?.value?.alt}`,
        isVideo: model.useVideo,
        videoUrl: model.video?.value?.href,
      },
    ];
  }, [model, thumbnailImgSrc]);

  const badgeLink = model.badgeLink?.value?.href;

  return (
    <>
      {model.badgeLink?.value?.text && (
        <div
          className={`card-label ${isEE || (badgeLink && badgeLink !== 'http://' && badgeLink !== 'https://') ? '' : 'card-label-text'}`}
        >
          <CustomExternalAnchor field={model.badgeLink} target={targetLinkdsk(model?.badgeLink, isWide)} />
        </div>
      )}
      <div className={`special-offers-card special-offers-card--${model.type?.value === 'offerCardWide' ? 'large' : 'small'}`}>
        <div className="card-with-header-image card-carousel__card">
          <div className={`card-carousel__image-wrapper ${model.useVideo ? 'card-carousel__image-wrapper--video' : ''}`}>
            <MediaElement
              hasWideCard={model.type?.value === 'offerCardWide'}
              mobileImage={mobileImageOrDefaultImage()}
              desktopImage={desktopImageOrDefaultImage()}
              thumbnailImgSrc={thumbnailImgSrc}
              useVideo={model.useVideo}
              isWide={isWide}
              isEE={isEE}
            />
            {model.useVideo && (
              <div onClick={showOverlayVideo} className="special-offers-card__video-play-button">
                <Icon iconName="action-play" size={32} color="white" />
              </div>
            )}
          </div>
          <div className="card-carousel__content">
            <div className="card-carousel__body">
              <span>
                {model.title && <CustomText tag="div" field={model.title} className="card-carousel__body--title" />}
                {model.subtitle && (
                  <RichText tag="div" field={model.subtitle} className="card-carousel__body--desc rich-text color-inherit-link" />
                )}
              </span>
            </div>
            <CardCarouselSlideButtons
              validButtonPrimaryExists={model.validButtonPrimaryExists}
              buttonPrimary={model.buttonPrimary}
              isWide={isWide}
              buttonSecondary={model.buttonSecondary}
              iconimage={iconimage}
              downloadButtonLink={model.downloadButtonLink}
              validButtonSecondaryExists={model.validButtonSecondaryExists}
              isOfferCardWide={model.type?.value === 'offerCardWide'}
              isEE={isEE ?? false}
              model={model}
              handleSplitModuleModal={handleSplitModuleModal}
            />
          </div>
        </div>
      </div>
      {model.video && model.useVideo && (
        <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
          <OverlayCarouselPopup slides={getOverlayProps()} />
        </ModalDialog>
      )}
      {model?.showPopupWithSplitModule && model?.splitModules && (
        <ModalDialog isOpen={showSplitModule} close={() => handleSplitModuleModal(false)} className="split-module-modal">
          {model?.splitModules.map((splitModule, index) => (
            <SplitModuleContainer {...splitModule} key={`split-Module-${index}`} params={{ imagePosition: index % 2 === 0 ? 'right' : 'left' }} />
          ))}
        </ModalDialog>
      )}
    </>
  );
};

export default CarouselSlide;
